import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { normalizeLinkKey } from 'tdr-common';

/**
 * Query param carrying the key.
 *
 * A query param rather than a fragment because `serializeSearchFiltersForURL` seeds from
 * `window.location.search`, so a param survives every in-app navigation without further
 * plumbing — and because link shorteners, email click trackers and server-side redirects all
 * drop fragments, which is the path a campaign link takes.
 */
export const LINK_KEY_PARAM = 'cond';

/** Session storage is per-tab, which is the lifetime we want: a keyed link scopes that visit. */
const storageKey = (restaurantId: string | undefined) => `tablz.linkKey.${restaurantId ?? 'unknown'}`;

function read(restaurantId: string | undefined): string | undefined {
	try {
		return normalizeLinkKey(sessionStorage.getItem(storageKey(restaurantId)));
	}
	catch {
		// Storage can throw in private modes; a missing key just means no keyed experience.
		return undefined;
	}
}

function write(restaurantId: string | undefined, value: string | undefined) {
	try {
		if (value) {
			sessionStorage.setItem(storageKey(restaurantId), value);
		}
		else {
			sessionStorage.removeItem(storageKey(restaurantId));
		}
	}
	catch {
		// Non-fatal: the key simply won't outlive this page.
	}
}

/**
 * The link key in force for this visit.
 *
 * `?cond=<key>` sets it and it then persists for the tab, so it survives navigation to pages
 * carrying no parameter. An explicitly empty `?cond=` clears it, which is the escape hatch back
 * to the normal results page.
 *
 * Keyed per restaurant so a key picked up at one venue cannot follow the guest to another.
 */
export function useLinkKey(restaurantId: string | undefined): string | undefined {
	const [searchParams] = useSearchParams();

	const raw = searchParams.get(LINK_KEY_PARAM);
	const incoming = normalizeLinkKey(raw);
	const explicitlyCleared = raw !== null && incoming === undefined;

	// Read storage during render rather than in an effect: the results page resolves its sections
	// on the first pass, and a key that arrived a tick later would flash the unkeyed layout first.
	const active = explicitlyCleared ? undefined : incoming ?? read(restaurantId);

	useEffect(() => {
		if (explicitlyCleared) {
			write(restaurantId, undefined);
		}
		else if (incoming) {
			write(restaurantId, incoming);
		}
	}, [restaurantId, incoming, explicitlyCleared]);

	return active;
}
